import { createApi } from '@reduxjs/toolkit/query/react';

import { apiGocardlessMandates } from '~constants/api';

import { Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import {
  GocardlessMandate,
  GocardlessMandateListQuery,
  GocardlessMandateListResponse,
  GocardlessMandateMatch,
} from '~direct-debits/models';

interface LinkMandateArgs {
  mandate_id: string;
  client_id: number;
}

interface IgnoreMandateArgs {
  mandate_id: string;
}

interface RestoreMandateArgs {
  mandate_id: string;
}

interface UnlinkMandateArgs {
  mandate_id: string;
}

interface IgnoreOrRestoreResponse {
  mandate: Pick<GocardlessMandate, 'id' | 'is_ignored'>;
}

interface UnlinkResponse {
  deleted: boolean;
}

interface LinkResponse {
  match: GocardlessMandateMatch & { mandate_id: string };
}

export const mandatesApi = createApi({
  reducerPath: 'directDebitsMandatesApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiGocardlessMandates }),
  tagTypes: ['mandates'],
  endpoints: (builder) => ({
    getMandates: builder.query<GocardlessMandateListResponse, GocardlessMandateListQuery & Request>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [{ type: 'mandates', id: 'LIST' }],
      transformResponse: (payload) => unwrap<GocardlessMandateListResponse>(payload),
    }),
    linkMandate: builder.mutation<LinkResponse, LinkMandateArgs>({
      query: ({ mandate_id, client_id }) => ({
        url: `/${mandate_id}/match`,
        method: 'POST',
        body: { client_id },
      }),
      invalidatesTags: [{ type: 'mandates', id: 'LIST' }],
      transformResponse: (payload) => unwrap<LinkResponse>(payload),
    }),
    unlinkMandate: builder.mutation<UnlinkResponse, UnlinkMandateArgs>({
      query: ({ mandate_id }) => ({
        url: `/${mandate_id}/match`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'mandates', id: 'LIST' }],
      transformResponse: (payload) => unwrap<UnlinkResponse>(payload),
    }),
    ignoreMandate: builder.mutation<IgnoreOrRestoreResponse, IgnoreMandateArgs>({
      query: ({ mandate_id }) => ({
        url: `/${mandate_id}/ignore`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'mandates', id: 'LIST' }],
      transformResponse: (payload) => unwrap<IgnoreOrRestoreResponse>(payload),
    }),
    restoreMandate: builder.mutation<IgnoreOrRestoreResponse, RestoreMandateArgs>({
      query: ({ mandate_id }) => ({
        url: `/${mandate_id}/restore`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'mandates', id: 'LIST' }],
      transformResponse: (payload) => unwrap<IgnoreOrRestoreResponse>(payload),
    }),
  }),
});

export const {
  useGetMandatesQuery,
  useLinkMandateMutation,
  useUnlinkMandateMutation,
  useIgnoreMandateMutation,
  useRestoreMandateMutation,
} = mandatesApi;
